/** Outcome of an account-deletion attempt: the modal closes and logs out only on `deleted`. */
export type DeleteAccountOutcome = { status: 'deleted' } | { status: 'failed'; reason: string };

export interface DeleteAccountDeps {
  /** Calls `delete-user-account`; resolves with the raw action response, rejects on a transport error. */
  deleteAccount: () => Promise<unknown>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const messageOf = (value: unknown, fallback: string): string => {
  if (typeof value === 'string' && value) return value;
  if (isRecord(value) && typeof value.message === 'string' && value.message) return value.message;
  return fallback;
};

/**
 * The backend answers HTTP 201 even when the deletion failed (`{ success: false, error }`), so only an
 * explicit `success: true` counts as deleted. A thrown error (network, timeout) is a failure too.
 */
export async function runAccountDeletion({ deleteAccount }: DeleteAccountDeps): Promise<DeleteAccountOutcome> {
  try {
    const response = await deleteAccount();
    if (isRecord(response) && response.success === true) return { status: 'deleted' };
    return { status: 'failed', reason: isRecord(response) ? messageOf(response.error, 'Account deletion failed') : 'Account deletion failed' };
  } catch (error) {
    return { status: 'failed', reason: messageOf(error, 'Account deletion failed') };
  }
}
